import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import moment from "moment-timezone";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ContractNature,
  Currency,
  useCreateBasicSourcingEventMutation,
  useCreateSourcingEventMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { NumericInput } from "../../../../common/input/NumericInput";
import { RadioOptionGroup } from "../../../../common/input/RadioOptionGroup";
import { urls } from "../../../../urls";
import { CONTRACT_NATURE_OPTIONS } from "./utils/publicProcurementOptions";
import { modalRoutingBanner, routesToSimplifiedFlow } from "./utils/foaPartDisplay";

export const CreatePublicSourcingEventModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { trigger } = useApiError();
  const [createBasic, { isLoading: isCreatingBasic }] = useCreateBasicSourcingEventMutation();
  const [createPublic, { isLoading: isCreatingPublic }] = useCreateSourcingEventMutation();

  const [title, setTitle] = useState("");
  const [estimatedValue, setEstimatedValue] = useState<number | null>(null);
  const [contractNature, setContractNature] = useState<ContractNature | "">("");

  const isSubmitting = isCreatingBasic || isCreatingPublic;
  const titleValid = title.trim().length >= 2;
  const valueValid = estimatedValue != null;
  const canSubmit = titleValid && valueValid && !isSubmitting;
  const banner = modalRoutingBanner(estimatedValue, contractNature || null);

  const submit = async () => {
    if (!canSubmit) return;
    try {
      if (routesToSimplifiedFlow(estimatedValue)) {
        const bse = await createBasic({
          depId: selectedOrg.departments[0].id,
          createSourcingEventRequest: {
            title: title.trim(),
            timezone: moment.tz.guess(),
            deadline: moment().endOf("day").set("ms", 0).set("s", 0).toISOString(),
          },
        }).unwrap();
        onClose();
        navigate(urls.events.edit.go(bse.id).intro, { replace: true });
      } else {
        const event = await createPublic({
          createSourcingEventV2Request: {
            title: title.trim(),
            estimatedValue: { value: estimatedValue, currency: Currency.Nok },
            contractNature: contractNature || null,
          },
        }).unwrap();
        onClose();
        navigate(urls.publicEvents.edit.go(event.id).classification);
      }
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("New procurement")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="6">
          <FormControl isRequired mb="5">
            <FormLabel>{t("Title")}</FormLabel>
            <Input
              value={title}
              placeholder={t("E.g. Office supplies for municipality of Oslo") ?? undefined}
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormControl>

          <FormControl isRequired mb="5">
            <FormLabel>{t("Estimated value")}</FormLabel>
            <InputGroup>
              <NumericInput
                numericValue={estimatedValue}
                placeholder="0"
                onNumericChange={setEstimatedValue}
              />
              <InputRightAddon>{t("NOK")}</InputRightAddon>
            </InputGroup>
            {!valueValid && (
              <FormHelperText>{t("Enter an estimated value to continue")}</FormHelperText>
            )}
          </FormControl>

          <FormControl mb="2">
            <FormLabel>{t("Contract type")}</FormLabel>
            <RadioOptionGroup
              options={CONTRACT_NATURE_OPTIONS}
              value={contractNature}
              allowDeselect
              onChange={(value) => setContractNature(value ? (value as ContractNature) : "")}
            />
          </FormControl>

          {banner && (
            <Alert status={banner.status} mt="4" borderRadius="md" fontSize="sm">
              <AlertIcon />
              <Box>{t(banner.messageKey)}</Box>
            </Alert>
          )}

          <Flex mt="6" justify="end">
            <Button
              colorScheme="teal"
              onClick={submit}
              isDisabled={!canSubmit}
              isLoading={isSubmitting}
            >
              {t("Create & continue")}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
